import { User } from '@brighte/passwordless-authenticator';
import { ApplicantType, PackageQuote } from '@graphql';
import { AnalyticsBrowser } from '@segment/analytics-next';
import { ANALYTICS_CHANNEL, AnalyticsEvent, CUSTOMER_CONNECT_SOURCE } from './constants';
import { AnalyticsBaseEventsPayload, AnalyticsEventsPayload } from './types';

type TransformBaseAnalyticsPayloadProps = {
  opportunityId: string;
  financialProductId?: string;
  quotes?: Array<PackageQuote>;
  user?: User;
};

type TransformEventAnalyticsPayloadProps = {
  eventName: string;
  quotes?: Array<PackageQuote>;
};

// this can be shared
export const analytics = AnalyticsBrowser.load({
  writeKey: import.meta.env.VITE_SEGMENT_WRITEKEY,
});

export const identifyAnalyticsUser = (user: string, properties?: Record<string, unknown>) =>
  analytics.identify(user, properties);

export const trackAnalyticsEvent = (event: AnalyticsEvent, properties?: Record<string, unknown>) =>
  analytics.track(event, { event_source: CUSTOMER_CONNECT_SOURCE, ...properties });

export const pageAnalyticsEvent = (pageName: string, properties?: Record<string, string>) =>
  analytics.page(pageName, properties);

export const transformBaseAnalyticsPayload = ({
  opportunityId,
  financialProductId,
  quotes,
  user,
}: TransformBaseAnalyticsPayloadProps): Partial<AnalyticsBaseEventsPayload> | null => {
  return {
    opportunity_id: opportunityId,
    user_id: user?.uid,
    role_type: user?.role,
    email: user?.email,
    mobile_number: user?.phone_number,
    applicant_type: ApplicantType.Primary,
    loan_type: financialProductId,
    loan_category: quotes?.[0]?.financialProduct?.type,
    customer_type: quotes?.[0]?.financialProduct?.customerType,
    event_source: CUSTOMER_CONNECT_SOURCE,
    ...(financialProductId && financialProductId in ANALYTICS_CHANNEL
      ? { channel: ANALYTICS_CHANNEL[financialProductId] }
      : {}),
  };
};

export const transformEventAnalyticsPayload = ({
  eventName,
  quotes,
}: TransformEventAnalyticsPayloadProps): Array<AnalyticsEventsPayload> | null => {
  if (!quotes) return null;

  switch (eventName) {
    case AnalyticsEvent.SubmittedCustomerPackageCategory:
    case AnalyticsEvent.SelectedPackage:
    case AnalyticsEvent.ViewedPackage:
      return quotes.map(quote => ({
        quote_id: quote.id,
        tracking_id: quote?.trackingId,
        application_loan_value: quote?.financedAmount,
        application_purchase_amount: quote?.totalPurchaseAmount,
        deposit_amount: quote?.depositAmount,
        vendor_id: quote?.vendor?.publicId,
        vendor_name: quote?.vendor?.tradingName,
        product_type: quote?.category?.name,
        product_description: quote?.productDescription,
      }));
    default:
      return null;
  }
};
